import PropTypes from 'prop-types';
import { forwardRef, useEffect, useMemo, useState } from 'react';
// @mui
import Link from '@mui/material/Link';
import Box from '@mui/material/Box';
// routes
import { RouterLink } from 'src/routes/components';
import axiosInstance from 'src/api/axiosInstance';
import { usePlanFeature } from 'src/hooks/usePlanFeature';
import { getAssetUrl, getTenantSlug } from 'src/utils/domainContext';

// ----------------------------------------------------------------------

const TENANT_LOGO_CACHE_PREFIX = 'tenantLogo:';
const tenantLogoRequests = new Map();

function getCachedTenantLogo(tenantSlug) {
  try {
    const cached = localStorage.getItem(`${TENANT_LOGO_CACHE_PREFIX}${tenantSlug}`);
    return cached ? JSON.parse(cached) : null;
  } catch (error) {
    console.error('Failed to read cached tenant logo:', error);
    return null;
  }
}

function cacheTenantLogo(tenantSlug, logoPath) {
  try {
    localStorage.setItem(
      `${TENANT_LOGO_CACHE_PREFIX}${tenantSlug}`,
      JSON.stringify({ logoPath, updatedAt: Date.now() })
    );
  } catch (error) {
    console.error('Failed to cache tenant logo:', error);
  }
}

function fetchTenantLogo(tenantSlug, headers) {
  if (tenantLogoRequests.has(tenantSlug)) {
    return tenantLogoRequests.get(tenantSlug);
  }

  const request = axiosInstance
    .get('/rest/preferences/logoPaths', { headers })
    .then((response) => {
      const preferences = Array.isArray(response.data) ? response.data : [response.data];

      const preference =
        preferences.find((item) => item?.logoPath || item?.logoPath1) || preferences[0] || null;

      const logoPath = preference?.logoPath1 || preference?.logoPath || '';

      cacheTenantLogo(tenantSlug, logoPath);

      return { logoPath, preference };
    })
    .finally(() => {
      tenantLogoRequests.delete(tenantSlug);
    });

  tenantLogoRequests.set(tenantSlug, request);

  return request;
}

// ----------------------------------------------------------------------

const Logo = forwardRef(
  ({ disabledLink = false, variant = 'full', disableTenantLogo = false, sx, ...other }, ref) => {
    const token = sessionStorage.getItem('token');

    const { enabled: planCustomLogoEnabled } = usePlanFeature('CUSTOM_LOGO');

    const tenantSlug = getTenantSlug();

    // Tenant logo is disabled when disableTenantLogo is true.
    const tenantSpecificBrandingEnabled =
      !disableTenantLogo && Boolean(tenantSlug) && planCustomLogoEnabled;

    const [logoState, setLogoState] = useState(() => {
      const cached = tenantSlug ? getCachedTenantLogo(tenantSlug) : null;

      return {
        tenantSlug,
        logoPath: cached?.logoPath || '',
      };
    });

    const [logoLoading, setLogoLoading] = useState(() => {
      const cached = tenantSlug ? getCachedTenantLogo(tenantSlug) : null;

      return Boolean(
        tenantSlug && planCustomLogoEnabled && !disableTenantLogo && !cached?.logoPath
      );
    });

    useEffect(() => {
      let active = true;

      const fetchData = async () => {
        try {
          // Do not fetch tenant logo when disabled.
          if (!tenantSpecificBrandingEnabled) {
            setLogoState({ tenantSlug, logoPath: '' });
            setLogoLoading(false);
            return;
          }

          const cached = getCachedTenantLogo(tenantSlug);

          if (cached) {
            setLogoState({
              tenantSlug,
              logoPath: cached.logoPath || '',
            });
          }

          setLogoLoading(!cached?.logoPath);

          const headers = {};

          if (token) {
            headers.Authorization = `Bearer ${token}`;
          }

          const { logoPath, preference } = await fetchTenantLogo(tenantSlug, headers);

          if (!active) return;

          if (preference) {
            const selectedPreferences = {
              preferencesId: preference.preferencesId,
              candidateInterviewTime: preference.candidateInterviewTime,
            };

            sessionStorage.setItem('selectedPreferences', JSON.stringify(selectedPreferences));

            localStorage.setItem('selectedPreferences', JSON.stringify(selectedPreferences));
          }

          setLogoState({ tenantSlug, logoPath });
        } catch (error) {
          console.error('Failed to fetch tenant logo:', error);
        } finally {
          if (active) {
            setLogoLoading(false);
          }
        }
      };

      fetchData();

      return () => {
        active = false;
      };
    }, [token, tenantSlug, tenantSpecificBrandingEnabled]);

    // Provider fallback logo.
    const fallbackLogoSrc = useMemo(
      () =>
        variant === 'short'
          ? getAssetUrl('/assets/MeruDeskShort.png')
          : getAssetUrl('/assets/MeruDesk.png'),
      [variant]
    );

    const logoStateMatchesTenant = logoState.tenantSlug === tenantSlug;

    const logoPaths = logoStateMatchesTenant ? logoState.logoPath : '';

    const customLogoPath = tenantSpecificBrandingEnabled ? logoPaths : '';

    const isLoadingTenantLogo =
      tenantSpecificBrandingEnabled && (!logoStateMatchesTenant || logoLoading);

    const currentLogoSrc = useMemo(
      () => customLogoPath || fallbackLogoSrc,
      [customLogoPath, fallbackLogoSrc]
    );

    const mergedSx = useMemo(() => {
      const isShort = variant === 'short';
      const hasCustomLogo = !!customLogoPath;

      const baseSx = {
        width: isShort ? 'auto' : '100%',
        height: 'auto',
        maxWidth: isShort ? 54 : 200,
        maxHeight: isShort ? 40 : 80,
        objectFit: 'contain',
        objectPosition: isShort ? 'center' : 'left center',
        margin: isShort ? '0 auto' : '0',
        cursor: 'pointer',
        display: 'block',
      };

      const layoutSx = { ...sx };

      if (hasCustomLogo) {
        if (layoutSx.height === 32 && layoutSx.width === 'auto') {
          delete layoutSx.height;
          delete layoutSx.width;

          layoutSx.maxWidth = isShort ? 54 : 220;
          layoutSx.maxHeight = isShort ? 36 : 45;
        }
      }

      return {
        ...baseSx,
        ...layoutSx,
      };
    }, [sx, variant, customLogoPath]);

    const logo = isLoadingTenantLogo ? (
      <Box
        ref={ref}
        component="span"
        role="img"
        aria-label="Loading tenant logo"
        sx={{
          ...mergedSx,
          height: variant === 'short' ? 40 : 48,
          minHeight: variant === 'short' ? 40 : 48,
          borderRadius: 1,
          backgroundColor: 'action.hover',
        }}
        {...other}
      />
    ) : (
      <Box
        ref={ref}
        component="img"
        src={currentLogoSrc}
        alt="Logo"
        sx={mergedSx}
        onError={() => {
          if (customLogoPath) {
            cacheTenantLogo(tenantSlug, '');

            setLogoState((current) =>
              current.tenantSlug === tenantSlug ? { ...current, logoPath: '' } : current
            );
          }
        }}
        {...other}
      />
    );

    if (disabledLink) {
      return logo;
    }

    return (
      <Link component={RouterLink} href="/dashboard" sx={{ display: 'contents' }}>
        {logo}
      </Link>
    );
  }
);

Logo.propTypes = {
  disabledLink: PropTypes.bool,
  variant: PropTypes.oneOf(['full', 'short']),
  disableTenantLogo: PropTypes.bool,
  sx: PropTypes.object,
};

export default Logo;
